import {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Button, Paper, Step, StepButton, StepLabel, Stepper, Typography} from "@mui/material";
import {ThemeProvider} from "@mui/material/styles";
import {LoadingButton} from "@mui/lab";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import {useNavigate} from "react-router";
import {useLocation, useSearchParams} from "react-router-dom";
import {useSelector} from "react-redux";

import BasicStep from "./AbTestSteps/BasicStep";
import AudienceStep from "./AbTestSteps/AudienceStep";
import CohortStep from "./AbTestSteps/CohortStep";
import SummaryStep from "./AbTestSteps/SummaryStep";
import BeforeSaveDialog from "./BeforeSaveDialog";
import ResultDialog from "./ResultDialog";
import {useSelectAbTestByIdQuery} from "../../core/reducers/abtest";
import {MODE} from "../Campaign/campaignMode";
import {
    addAudience,
    addParameter,
    cloneAudience,
    deleteAudience,
    deleteParameter,
    setParameterData,
    updateParameter,
    validateAudiences
} from "../Campaign/campaignFormModel";
import {
    addCohort,
    applyDate,
    applyField,
    buildSavePayload,
    createAbTest,
    deleteCohort,
    getNextBlockReason,
    ID_CHECK_DELAY,
    normalizeRangeInput,
    STEP_ORDER,
    STEPS,
    updateCohort,
    validateBasic,
    validateCohorts
} from "./abTestFormModel";
import {getColor, getEnvName} from "../Utils/CommonUtils";
import {useCompactFormTheme} from "../Utils/compactFormTheme";
import {getDomainAlias} from "../../constants";
import {resolvePublisherReturnTo} from "../Publisher/Campaign/CampaignFilterState";

// FormWrapper создаёт новый AB-тест через `new AbTest()`; даты считаются в момент создания
export class AbTest {
    constructor() {
        Object.assign(this, createAbTest({domainAlias: getDomainAlias()}));
    }
}

// используется CohortSimple (мини-форма AB-теста)
export class CohortError {
    constructor(cohortIndex, errorType, corrected, uuid) {
        this.cohortIndex = cohortIndex;
        this.errorType = errorType;
        this.corrected = corrected;
        this.uuid = uuid;
    }
}

/**
 * Конструктор AB-теста. Режимы: CREATE, UPDATE, CLONE.
 * Все ошибки вычисляются из текущего состояния (раньше — накапливались в массивах и могли "залипать").
 */
export default function AbTestForm({mode, title, abTest}) {
    const [searchParams] = useSearchParams();
    const sourceMode = useSelector((state) => state.data.SourceMode);
    const envName = getEnvName('env', sourceMode, searchParams);
    const navigate = useNavigate();
    const location = useLocation();
    const formTheme = useCompactFormTheme();
    const domainAlias = getDomainAlias();

    const [activeStep, setActiveStep] = useState(0);
    const [currentAbTest, setCurrentAbTest] = useState(abTest);
    const [abTestIdForCheck, setAbTestIdForCheck] = useState();
    const [saveAbTestResponse, setSaveAbTestResponse] = useState({});
    const [beforeSaveDialog, setBeforeSaveDialog] = useState(false);
    const [abTestForSave, setAbTestForSave] = useState({});
    const step = STEP_ORDER[activeStep];
    const update = useCallback(updater => setCurrentAbTest(current => updater(current)), []);

    // ---- проверка занятости id (с задержкой, как раньше — 1 c) ----
    useEffect(() => {
        const timer = setTimeout(() => setAbTestIdForCheck(currentAbTest.abTestId), ID_CHECK_DELAY);
        return () => clearTimeout(timer);
    }, [currentAbTest.abTestId]);
    const {status: checkingStatus} = useSelectAbTestByIdQuery(
        {env: envName, id: abTestIdForCheck},
        {skip: !abTestIdForCheck || mode === MODE.UPDATE}
    );
    const idCheck = useMemo(() => {
        if (mode === MODE.UPDATE) return {pending: false, isFree: true};
        const pending = abTestIdForCheck !== currentAbTest.abTestId || checkingStatus === 'pending' || checkingStatus === 'uninitialized';
        // id свободен, если поиск не нашёл AB-тест
        return {pending, isFree: !pending && checkingStatus === 'rejected'};
    }, [mode, abTestIdForCheck, currentAbTest.abTestId, checkingStatus]);

    // ---- валидация (всё производное от состояния) ----
    const basicErrors = useMemo(() => validateBasic(currentAbTest, {mode, domainAlias}), [currentAbTest, mode, domainAlias]);
    const basicErrorFields = useMemo(() => {
        const fields = [...basicErrors];
        if (!idCheck.pending && !idCheck.isFree && mode !== MODE.UPDATE) fields.push('abTestId');
        return fields;
    }, [basicErrors, idCheck, mode]);
    const audienceValidation = useMemo(() => validateAudiences(currentAbTest.audiences), [currentAbTest.audiences]);
    const cohortValidation = useMemo(() => validateCohorts(currentAbTest.cohorts), [currentAbTest.cohorts]);
    const abTestForView = useMemo(
        () => audienceValidation.audiences === currentAbTest.audiences ? currentAbTest : {...currentAbTest, audiences: audienceValidation.audiences},
        [currentAbTest, audienceValidation.audiences]
    );

    const blockReason = getNextBlockReason({
        step,
        mode,
        basicErrors,
        idCheck,
        audienceHasErrors: audienceValidation.hasErrors,
        cohortsHaveErrors: cohortValidation.hasErrors,
    });
    const nextLoading = step === STEPS.BASIC && mode !== MODE.UPDATE && idCheck.pending && basicErrors.size === 0;

    // ---- обработчики ----
    const handleChange = (event) => {
        const {name, value, checked} = event.target;
        update(current => applyField(current, name, value, checked));
    };
    const handleDateChange = (date, field) => update(current => applyDate(current, field, date));

    const audienceHandlers = useMemo(() => ({
        handleAddNewAudience: () => update(addAudience),
        handleCloneAudience: (index) => update(current => cloneAudience(current, index)),
        handleDeleteAudience: (index) => update(current => deleteAudience(current, index)),
        handleAddNewParams: (audienceIndex) => update(current => addParameter(current, audienceIndex)),
        handleDeleteParams: (audienceIndex, paramIndex) => update(current => deleteParameter(current, audienceIndex, paramIndex)),
        handleParameterDataChange: (params, value, paramIndex, audienceIndex) =>
            update(current => setParameterData(current, params, value, audienceIndex, paramIndex)),
        handleParameterOperatorChange: (value, paramIndex, audienceIndex) =>
            update(current => updateParameter(current, audienceIndex, paramIndex, {operator: value})),
        handleParameterValueChange: (value, paramIndex, audienceIndex) =>
            update(current => updateParameter(current, audienceIndex, paramIndex, {paramValue: value})),
        handleManualInput: (e, paramIndex, audienceIndex) =>
            update(current => updateParameter(current, audienceIndex, paramIndex, {paramValue: e.target.value})),
        handleManualInputForOffset: (e, fieldName, paramIndex, audienceIndex) =>
            update(current => updateParameter(current, audienceIndex, paramIndex, {[fieldName]: e.target.value})),
        handleVersionNumericParameterChange: (field, value, paramIndex, audienceIndex) => update(current => {
            const parameterSet = current.audiences[audienceIndex].parameters[paramIndex];
            return updateParameter(current, audienceIndex, paramIndex, {[field]: normalizeRangeInput(parameterSet, field, value)});
        }),
    }), [update]);

    const cohortHandlers = useMemo(() => ({
        onChange: (index, field, value) => update(current => updateCohort(current, index, field, value)),
        onAdd: () => update(addCohort),
        onDelete: (index) => update(current => deleteCohort(current, index)),
    }), [update]);

    const handleNext = () => {
        if (blockReason) return;
        if (step !== STEPS.SUMMARY) {
            setActiveStep(activeStep + 1);
            return;
        }
        setAbTestForSave(buildSavePayload(currentAbTest));
        setBeforeSaveDialog(true);
    };
    const handleBack = () => setActiveStep(activeStep - 1);
    const handleGoToPublisher = () => {
        const fallback = `/publisher/abtests?env=${sourceMode.env1Name}&env2=${sourceMode.env2Name}`;
        navigate(resolvePublisherReturnTo(location.state, fallback));
    };

    const getStepContent = () => {
        switch (step) {
            case STEPS.BASIC:
                return <BasicStep
                    mode={mode}
                    currentAbTest={currentAbTest}
                    handleChange={handleChange}
                    errorFields={basicErrorFields}
                    handleDateChange={handleDateChange}
                />;
            case STEPS.AUDIENCE:
                return <AudienceStep
                    currentAbTest={abTestForView}
                    setCurrentAbTest={setCurrentAbTest}
                    errorAudienceIndexes={audienceValidation.errorAudienceIndexes}
                    errorFields={audienceValidation.rangeErrors}
                    {...audienceHandlers}
                />;
            case STEPS.COHORTS:
                return <CohortStep
                    cohorts={currentAbTest.cohorts}
                    validation={cohortValidation}
                    {...cohortHandlers}
                />;
            case STEPS.SUMMARY:
                return <SummaryStep currentAbTest={currentAbTest}/>;
            default:
                throw new Error('Unknown step');
        }
    };

    return (
        <ThemeProvider theme={formTheme}>
            <Box display={'flex'} justifyContent={'center'}>
                <Box component="main" maxWidth="md" width="100%" sx={{mb: 4}}>
                    <Paper variant="outlined" sx={{my: 2, p: {xs: 2, md: 3}}}>
                        <Typography component="h1" variant="h5" align="center">
                            <span style={{color: getColor(mode)}}>
                                {mode.toUpperCase() + (mode === MODE.CREATE ? ' AB test' : '')}
                            </span> {title ? title : ''}
                        </Typography>
                        <Stepper nonLinear activeStep={activeStep} sx={{pt: 2, pb: 3}}>
                            {STEP_ORDER.map((label, index) => (
                                <Step key={label} completed={index < activeStep}>
                                    {index < activeStep
                                        // к пройденным шагам можно вернуться кликом
                                        ? <StepButton onClick={() => setActiveStep(index)}>{label}</StepButton>
                                        : <StepLabel>{label}</StepLabel>}
                                </Step>
                            ))}
                        </Stepper>

                        {getStepContent()}

                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 3}}>
                            {activeStep !== 0
                                ? <Button onClick={handleBack} startIcon={<NavigateBeforeIcon/>}>Back</Button>
                                : <Button startIcon={<NavigateBeforeIcon/>} color={"warning"} variant={"contained"} onClick={handleGoToPublisher}>
                                    To Publisher
                                </Button>}
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                {blockReason && !nextLoading &&
                                    <Typography variant="body2" color="text.secondary" className="ab-test-form__block-reason">
                                        {blockReason}
                                    </Typography>}
                                <LoadingButton
                                    loading={nextLoading}
                                    loadingPosition={'end'}
                                    endIcon={<NavigateNextIcon/>}
                                    variant="contained"
                                    onClick={handleNext}
                                    disabled={!!blockReason}
                                >
                                    {step === STEPS.SUMMARY ? 'Finish' : 'Next'}
                                </LoadingButton>
                            </Box>
                        </Box>
                        <BeforeSaveDialog
                            beforeSaveDialogOpen={beforeSaveDialog}
                            setBeforeSaveDialogOpen={setBeforeSaveDialog}
                            abTestForSave={abTestForSave}
                            setSaveAbTestResponse={setSaveAbTestResponse}
                        />
                        <ResultDialog saveAbTestResponse={saveAbTestResponse}/>
                    </Paper>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
